import { useLocation } from 'react-router-dom'

/**
 * The code namespace, which is also the backend URL prefix and a tracking-label prefix. It stays
 * `adm`/`pbs` even though the app's own URLs now say One and Node: renaming it would silently repoint
 * every API call and orphan years of PostHog labels.
 */
export type Product = 'pbs' | 'adm'

export const PATH_BY_PRODUCT: Record<Product, string> = {
  pbs: '/monetization/node',
  adm: '/monetization/one',
}

/**
 * `null` on the product-neutral pages under `/monetization/inventory`, where no product is implied.
 *
 * Matches the whole prefix rather than searching the path for a substring: a media or stack named
 * "node-something" appears in these URLs as a route param, and must not decide the product.
 */
export const useOptionalProduct = (): Product | null => {
  const { pathname } = useLocation()
  if (pathname === PATH_BY_PRODUCT.pbs || pathname.startsWith(`${PATH_BY_PRODUCT.pbs}/`)) {
    return 'pbs'
  }
  if (pathname === PATH_BY_PRODUCT.adm || pathname.startsWith(`${PATH_BY_PRODUCT.adm}/`)) {
    return 'adm'
  }
  return null
}

/**
 * Falls back to `adm` off a product path, as it always has. Anything mounted under
 * `/monetization/inventory` must use `useOptionalProduct` instead — the fallback there means One's API
 * answering for a Node page, which shows an empty list rather than an error.
 */
export const useProduct = (): { product: Product; productPath: string } => {
  const product = useOptionalProduct() ?? 'adm'
  return {
    product,
    productPath: PATH_BY_PRODUCT[product],
  }
}
